import { keepPreviousData, useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, courseInstructors, primaryInstructor, parseLiveClassTime } from '@astro/api'
import { useAuth } from '@astro/auth'
import { unwrapAstrologerProfile } from '@astro/astrologer-forms'
import { normalizeRoles } from './roles'
import {
  getPanchang, getPlanetTransits, getKundli, resolveLocation,
  buildTransitReading, planetTransitStatus, buildKundliChart,
  getHoroscopePredictions, getPlanetStrengths,
} from '@astro/vedastro'

/* ─────────────────────────────────────────────────────────────
   Dashboard data layer. Fetches the signed-in seeker's live data
   from the BFF-proxied API and normalises the loosely-typed
   upstream payloads into the exact shapes the tabs render.

   Every query is best-effort (retry:false): if an endpoint is
   unavailable the page silently keeps its polished demo content
   rather than showing an empty state.
   ───────────────────────────────────────────────────────────── */

/* ── small utilities ── */

/** Unwrap a Laravel `{ status, data }` envelope (or pass a bare value through). */
function unwrap(res) {
  if (res && typeof res === 'object' && 'data' in res) return res.data
  return res
}

/** Coerce a value into a plain list — handles arrays, envelopes and paginators. */
function toList(res) {
  const d = unwrap(res)
  if (Array.isArray(d)) return d
  if (d && Array.isArray(d.data)) return d.data // length-aware paginator
  return []
}

/** First-two-initials from a display name, e.g. "Aarav Sharma" → "AS". */
export function initials(name) {
  if (!name) return 'U'
  const parts = String(name).trim().split(/\s+/).filter(Boolean)
  const chars = parts.slice(0, 2).map((w) => w[0]).join('')
  return (chars || name[0]).toUpperCase()
}

/** Indian-format rupee amount, e.g. 2450 → "₹ 2,450". Blank-safe. */
export function formatINR(value) {
  const n = Number(value)
  if (!Number.isFinite(n)) return '—'
  return `₹ ${Math.round(n).toLocaleString('en-IN')}`
}

/** Karma points come in 0.5 steps, e.g. 12.5 → "12½", 0.5 → "½", 1200 → "1,200".
 *  A half is written as ½ because the display font's decimal point is nearly
 *  invisible — "12.5" reads as "125". Blank-safe. */
export function formatKarma(value) {
  const n = Number(value)
  if (!Number.isFinite(n)) return '—'
  const whole = Math.trunc(n)
  const frac = Math.abs(n - whole)
  if (frac === 0) return whole.toLocaleString('en-IN')
  if (frac === 0.5) return `${n < 0 && whole === 0 ? '-' : ''}${whole === 0 ? '' : whole.toLocaleString('en-IN')}½`
  return n.toLocaleString('en-IN', { maximumFractionDigits: 2 })
}

function toNumber(value) {
  if (value == null) return null
  const n = Number(String(value).replace(/[^0-9.-]/g, ''))
  return Number.isFinite(n) ? n : null
}

/** Membership pill copy — EVERY held role in precedence order (a user can
 *  hold several, e.g. "INSTRUCTOR · SEEKER"). Roles are normalised first
 *  (upstream 'user' → customer, case-insensitive) so raw auth slugs always
 *  match; the base customer role shows as SEEKER alongside other roles and
 *  as SEEKER MEMBER when it's all the user has. */
export function membershipLabel(roles = []) {
  const set = new Set(normalizeRoles(roles))
  const labels = []
  if (set.has('admin')) labels.push('ADMINISTRATOR')
  if (set.has('astrologer')) labels.push('ASTROLOGER')
  if (set.has('instructor')) labels.push('INSTRUCTOR')
  if (set.has('student')) labels.push('ACADEMY MEMBER')
  if (set.has('customer')) labels.push('SEEKER')
  if (labels.length === 1 && labels[0] === 'SEEKER') return 'SEEKER MEMBER'
  return labels.length ? labels.join(' · ') : 'SEEKER MEMBER'
}

const MONTHS = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC']

/** Normalise a stored DOB (various formats) into "YYYY-MM-DD", or null. */
export function normaliseDob(raw) {
  if (!raw) return null
  const s = String(raw).trim()
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10) // already ISO
  const d = new Date(s)
  if (Number.isNaN(d.getTime())) return null
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

/** Normalise a stored birth time (e.g. "07:42 AM") into 24h "HH:MM", or null. */
export function normaliseTime(raw) {
  if (!raw) return null
  const m = String(raw).trim().match(/^(\d{1,2}):(\d{2})\s*(AM|PM)?/i)
  if (!m) return null
  let h = Number(m[1])
  const min = m[2]
  const ap = m[3]?.toUpperCase()
  if (ap === 'PM' && h < 12) h += 12
  if (ap === 'AM' && h === 12) h = 0
  return `${String(h).padStart(2, '0')}:${min}`
}

const SIGN_GLYPH = {
  Aries: '♈', Taurus: '♉', Gemini: '♊', Cancer: '♋', Leo: '♌', Virgo: '♍',
  Libra: '♎', Scorpio: '♏', Sagittarius: '♐', Capricorn: '♑', Aquarius: '♒', Pisces: '♓',
}
const PLANET_ICON = { Sun: '☀️', Moon: '🌙', Mars: '♂️', Mercury: '☿', Venus: '♀', Jupiter: '♃', Saturn: '♄', Rahu: '☊' }

/** Build a Date from a booking's various date/time fields. Returns null if unparseable.
 *  A 12-hour `time` ("03:30 PM") can't be appended raw — it has to reach 24h form
 *  first, or the whole booking loses its date. A date with no time is pinned to
 *  local midnight, since a bare "YYYY-MM-DD" parses as UTC and would otherwise
 *  drag the booking onto the wrong day west of Greenwich. `scheduled_at` is
 *  passed through untouched so any zone it carries still applies. */
function bookingDate(b) {
  const time = b.time ? normaliseTime(b.time) || b.time : null
  const raw = b.scheduled_at || (b.date ? `${b.date}${time ? ` ${time}` : 'T00:00'}` : null)
  if (!raw) return null
  const d = new Date(raw.replace(' ', 'T'))
  return Number.isNaN(d.getTime()) ? null : d
}

const slotTime = (d) =>
  d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true }).toUpperCase()

/** Booked time slot "10:00 PM – 10:30 PM" — start plus duration, the same
 *  label the jyotish booking detail page shows. Start only when no duration. */
function slotLabel(d, duration) {
  if (!d) return null
  if (!duration || duration <= 0) return slotTime(d)
  return `${slotTime(d)} – ${slotTime(new Date(d.getTime() + duration * 60_000))}`
}

/** Counterpart display name — the astrologer on seeker rows, the client on astrologer rows. */
function astrologerName(b) {
  return b.astrologer?.display_name || b.astrologer?.name || b.astrologer_name || b.user?.name || 'Astrologer'
}

/** The booking customer (seeker) — the `user` relation on astrologer-side rows. */
function clientName(b) {
  const u = b.user || {}
  return (
    u.name ||
    [u.first_name, u.middle_name, u.last_name].filter(Boolean).join(' ') ||
    b.user_name || b.customer_name || null
  )
}

function statusStyle(status) {
  const s = String(status || '').toLowerCase()
  if (s.includes('cancel')) return { cls: 'bg-stone-100 text-stone-500', action: 'Rebook', join: false }
  if (s.includes('complete') || s.includes('done')) return { cls: 'bg-green-50 text-green-600', action: 'Report', join: false }
  return { cls: 'bg-blue-50 text-blue-600', action: 'Join', join: true }
}

/* ── normalisers → the shapes the existing tabs already consume ── */

/* Most live bookings carry no service/title — only consultation_type
   ("video" | "phone" | "inperson"). Raw, that reads as a nonsense card
   headline, so map it to a proper service name. */
const TYPE_TITLES = {
  video: 'Video Consultation',
  phone: 'Phone Consultation',
  call: 'Phone Consultation',
  chat: 'Chat Consultation',
  inperson: 'In-Person Consultation',
}

function serviceTitle(b) {
  if (b.service) return b.service
  if (b.title) return b.title
  const typeKey = String(b.consultation_type || '').toLowerCase().replace(/[^a-z]/g, '')
  return TYPE_TITLES[typeKey] || titleCase(b.consultation_type) || 'Consultation'
}

function normaliseConsultations(bookings) {
  return bookings.map((b, i) => {
    const d = bookingDate(b)
    const svc = serviceTitle(b)
    const st = statusStyle(b.status)
    const duration = toNumber(b.duration)
    const statusLabel = b.status
      ? String(b.status).charAt(0).toUpperCase() + String(b.status).slice(1)
      : 'Upcoming'
    const modeRaw = String(b.consultation_type || '').toLowerCase()
    // The API stores video | phone | inperson; `call` is the legacy spelling.
    const mode = modeRaw.includes('call') || modeRaw.includes('phone')
      ? '📞 Call'
      : modeRaw.includes('chat')
        ? '💬 Chat'
        : modeRaw.includes('inperson') || modeRaw.includes('in-person')
          ? '🤝 In-person'
          : '📹 Video'
    const amount = toNumber(b.amount ?? b.rate ?? b.price ?? b.total_amount)

    return {
      key: b.id ?? i,
      /** Raw booking id — links the card to the jyotish detail pages. */
      id: b.id ?? null,
      /** Astrologer id (astrologers.id, not the booking's user_id) — the
       * reviewable_id a completed booking's "Rate Consultation" link needs. */
      astrologerId: b.astrologer_id ?? b.astrologer?.id ?? null,
      // Booking card fields
      code: b.booking_code || b.booking_number || b.booking_id || (b.id != null ? `#${b.id}` : '—'),
      person: astrologerName(b),
      dateLabel: d ? d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—',
      amount: amount != null ? formatINR(amount) : null,
      /** Raw rupee amount — the astrologer overview sums these into earnings. */
      amountValue: amount,
      /** Customer (seeker) details — what astrologer-side cards display. */
      client: clientName(b),
      clientEmail: b.user?.email || null,
      clientMobile: b.user?.mobile_no || b.user?.mobile || null,
      /** Client birth details captured at booking time (may be absent) —
       *  normalised to input-ready forms for the astrologer kundli studio. */
      birthDate: normaliseDob(b.birth_date || b.birth_details?.birth_date),
      birthTime: normaliseTime(b.birth_time || b.birth_details?.birth_time),
      birthPlace: b.place || b.birth_place || b.birth_details?.place || null,
      /** Scheduled time as epoch ms (null when unparseable) — the sortable
       *  counterpart to the display-only day/mon/time strings below. */
      at: d ? d.getTime() : null,
      // Overview timeline card
      day: d ? String(d.getDate()).padStart(2, '0') : '—',
      mon: d ? MONTHS[d.getMonth()] : '',
      dark: i === 0,
      title: svc,
      with: `with ${astrologerName(b)}`,
      time: d ? d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }) : null,
      /** Booked slot (start – end) — the consultation card's time fact. */
      slot: slotLabel(d, duration),
      dur: duration ? `${duration} min` : null,
      join: st.join,
      // My Consultations table row
      svc,
      dt: d
        ? `${d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })} · ${d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}`
        : '—',
      mode,
      status: statusLabel,
      cls: st.cls,
      action: st.action,
      /** Astrologer's post-session report note — the Reports tab renders it. */
      note: b.astrologer_note || null,
      noteStatus: String(b.astrologer_note_status || '').toLowerCase() || null,
    }
  })
}

function orderStatusStyle(status) {
  const s = String(status || '').toLowerCase()
  if (s.includes('deliver') || s.includes('complete')) return 'bg-green-50 text-green-600'
  if (s.includes('ship') || s.includes('dispatch') || s.includes('transit')) return 'bg-blue-50 text-blue-600'
  if (s.includes('cancel') || s.includes('return') || s.includes('refund')) return 'bg-stone-100 text-stone-500'
  return 'bg-amber-50 text-amber-600' // pending / processing / placed
}

const PAYMENT_LABELS = { razorpay: 'Razorpay', cod: 'Cash on Delivery', cash_on_delivery: 'Cash on Delivery' }

function paymentStatusStyle(status) {
  const s = String(status || '').toLowerCase()
  if (s.includes('paid') || s.includes('success') || s.includes('captur') || s.includes('complete')) return 'bg-green-50 text-green-600'
  if (s.includes('fail') || s.includes('cancel') || s.includes('refund')) return 'bg-red-50 text-red-500'
  return 'bg-amber-50 text-amber-600' // pending / initiated
}

function titleCase(raw, fallback = '') {
  const s = String(raw || fallback).replace(/[_-]+/g, ' ').trim()
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : ''
}

/* Field names verified against the live GET /orders payload: status is
   `order_status`, the grand total is `total_amount` (decimal string), and
   each item snapshots `product_name`/`quantity`/`price`/`subtotal` with the
   full product nested under `product` (which carries no image fields on this
   endpoint). Money fields (`price_subtotal`, `price_gst`, `price_shipping`,
   `coupon_discount`, `total_amount`) are decimal strings. */
function normaliseOrders(orders) {
  return orders.map((o, i) => {
    const items = (o.items || []).map((it, j) => {
      const product = it.product || {}
      const qty = toNumber(it.quantity) ?? 1
      return {
        key: it.id ?? j,
        /** Used to hydrate the thumbnail from the catalog. */
        productId: it.product_id ?? product.id ?? null,
        name: it.product_name || product.name || 'Item',
        qty,
        unitPrice: formatINR(it.price),
        lineTotal: formatINR(it.subtotal ?? (toNumber(it.price) ?? 0) * qty),
        img: product.image_url || product.image || product.thumbnail || null,
      }
    })
    const when = o.created_at ? new Date(o.created_at) : null
    const date = when && !Number.isNaN(when.getTime())
      ? when.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
      : ''
    const number = o.order_number || o.id
    const subtotal = toNumber(o.price_subtotal)
    const gst = toNumber(o.price_gst) ?? 0
    const shipping = toNumber(o.price_shipping) ?? 0
    const total = toNumber(o.total_amount ?? o.discounted_price ?? o.price_subtotal)
    // Everything shaved off the pre-discount amount (item discounts + coupon).
    const savings = subtotal != null && total != null ? Math.max(0, subtotal + gst + shipping - total) : 0

    return {
      key: o.id ?? i,
      number: number != null ? `#${number}` : '',
      date,
      status: titleCase(o.order_status || o.status, 'placed'),
      cls: orderStatusStyle(o.order_status || o.status),
      items,
      itemCount: items.reduce((n, it) => n + it.qty, 0),
      payMethod: PAYMENT_LABELS[String(o.payment_method || '').toLowerCase()] || titleCase(o.payment_method) || null,
      payStatus: titleCase(o.payment_status) || null,
      payCls: paymentStatusStyle(o.payment_status),
      txnId: o.transaction_id || null,
      coupon: o.coupon_code || null,
      notes: o.order_notes || null,
      subtotal: subtotal == null ? null : formatINR(subtotal),
      gst: gst > 0 ? formatINR(gst) : null,
      shipping: shipping > 0 ? formatINR(shipping) : null,
      savings: savings >= 1 ? formatINR(savings) : null,
      total: total == null ? '—' : formatINR(total),
    }
  })
}

/** Astrologer directory record → the "Book a Session" card shape. */
function normaliseAstrologers(list) {
  return list.map((a, i) => {
    const rate = toNumber(a.rate)
    return {
      key: a.id ?? i,
      id: a.id ?? null,
      name: a.display_name || a.name || 'Astrologer',
      role: a.skills?.[0] || 'Vedic Astrologer',
      exp: a.experience != null ? `${a.experience} Yrs` : '—',
      rating: a.rating != null ? Number(a.rating).toFixed(1) : 'New',
      price: rate != null ? rate.toLocaleString('en-IN') : '—',
      status: a.online_status === 'online' ? 'live' : 'busy',
      tag: `✨ ${a.skills?.[0] || 'Astrology'}`,
      img: a.image_url || null,
    }
  })
}

/* Live-class status → pill styling. Postman documents scheduled|live|
   completed|cancelled; the course-detail payloads also use "upcoming",
   so match loosely and title-case anything unknown. */
function liveClassStatusStyle(status) {
  const s = String(status || '').toLowerCase()
  if (s.includes('live')) return { label: 'Live', cls: 'bg-red-50 text-red-500', live: true }
  if (s.includes('complete') || s.includes('end')) return { label: 'Completed', cls: 'bg-green-50 text-green-600', live: false }
  if (s.includes('cancel')) return { label: 'Cancelled', cls: 'bg-stone-100 text-stone-500', live: false }
  return { label: titleCase(status, 'scheduled'), cls: 'bg-blue-50 text-blue-600', live: false }
}

/** Date → "YYYY-MM-DDTHH:mm" — prefill for <input type="datetime-local">. */
function toLocalInput(d) {
  if (!d) return null
  const pad = (n) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
}

/** Live-class record (GET /live-classes) → the instructor overview's row shape. */
function normaliseLiveClasses(list) {
  return list.map((lc, i) => {
    // Wall-clock parse — the API's "Z" suffix on these fields is fake (see
    // parseLiveClassTime); trusting it shifted every time by +5:30 in IST.
    const startsAt = parseLiveClassTime(lc.start_time)
    const endsAt = parseLiveClassTime(lc.end_time)
    const durMin = startsAt && endsAt ? Math.round((endsAt - startsAt) / 60000) : null
    const st = liveClassStatusStyle(lc.status)
    return {
      key: lc.id ?? i,
      id: lc.id ?? null,
      title: lc.title || 'Live Class',
      /** Parent course title when the relation rides along; null for standalone classes. */
      course: lc.course?.title || null,
      /** Parent course id — stable grouping key for the "By Course" view
       * (two courses can share a title); null for standalone classes. */
      courseId: lc.course_id ?? lc.course?.id ?? null,
      day: startsAt ? String(startsAt.getDate()).padStart(2, '0') : '—',
      mon: startsAt ? MONTHS[startsAt.getMonth()] : '',
      dateLabel: startsAt
        ? startsAt.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
        : '—',
      time: startsAt ? startsAt.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }) : null,
      endTime: endsAt ? endsAt.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }) : null,
      /** datetime-local prefills for the reschedule form. */
      startLocal: toLocalInput(startsAt),
      endLocal: toLocalInput(endsAt),
      dur: durMin && durMin > 0 ? `${durMin} min` : null,
      status: st.label,
      cls: st.cls,
      isLive: st.live,
      rawStatus: String(lc.status || '').toLowerCase(),
      isRecorded: Boolean(lc.is_recorded),
      recordingUrl: lc.recording_url || null,
    }
  })
}

/** True when the signed-in user carries the astrologer role. */
export function isAstrologerUser(user) {
  return Boolean(user?.roles?.includes('astrologer'))
}

/** True when the signed-in user carries the instructor role. */
export function isInstructorUser(user) {
  return Boolean(user?.roles?.includes('instructor'))
}

/* ── per-tab hooks ── */

/**
 * Live astrologer directory for the Consultations tab's "Book a Session"
 * cards — the /filter read is the richest (carries online_status). Lazy:
 * fetched on the tab's first render; demo cards remain on failure.
 */
export function useAstrologers(enabled = true) {
  const query = useQuery({
    queryKey: ['dashboard', 'astrologers'],
    queryFn: () => api.astrologers.filter(),
    enabled,
    retry: false,
    staleTime: 60_000,
    select: (res) => normaliseAstrologers(toList(res)),
  })
  const astrologers = query.data ?? []
  return { astrologers, hasAstrologers: astrologers.length > 0, isLoading: enabled && query.isPending }
}

/**
 * Sessions booked WITH the signed-in astrologer — GET /astrologer/:id/bookings
 * keyed by their own user id. Fires only for astrologer accounts.
 */
export function useAstrologerBookings() {
  const { user } = useAuth()
  const enabled = isAstrologerUser(user) && Boolean(user?.id)
  const query = useQuery({
    queryKey: ['dashboard', 'astrologer-bookings', user?.id],
    queryFn: () => api.dashboard.astrologerBookings(user.id),
    enabled,
    retry: false,
    staleTime: 60_000,
    select: (res) => normaliseConsultations(toList(res)),
  })
  const bookings = query.data ?? []
  return { bookings, hasBookings: bookings.length > 0, isLoading: enabled && query.isPending }
}

/**
 * The signed-in astrologer's earnings — GET /astrologer/earnings, which costs
 * every consultation through the admin's commission settings and returns the
 * totals with it. The stat cards and the Earnings tab therefore quote the same
 * figures, and neither has to know what the commission rate is.
 *
 * `status` defaults to 'all' so the console's "Total Earnings" card counts the
 * whole practice (the Earnings tab asks for completed consultations only).
 */
export function useAstrologerEarnings(status = 'all') {
  const { user } = useAuth()
  const enabled = isAstrologerUser(user) && Boolean(user?.id)
  const query = useQuery({
    queryKey: ['dashboard', 'astrologer-earnings', { status }],
    queryFn: () => api.astrologers.earnings({ status }),
    enabled,
    retry: false,
    staleTime: 60_000,
    select: (res) => ({
      summary: res?.data?.summary ?? null,
      rates: res?.data?.rates ?? null,
    }),
  })
  return {
    summary: query.data?.summary ?? null,
    rates: query.data?.rates ?? null,
    isLoading: enabled && query.isPending,
  }
}

/**
 * Shop orders — used only by the Orders tab, so it fetches lazily on the
 * tab's first render instead of riding along with the page-level queries.
 * Paginated: GET /orders?page=N returns a Laravel paginator in a status
 * envelope. Tabs unmount on switch; staleTime keeps revisits from refetching.
 */
export function useOrders(page = 1, status = 'all') {
  const { user } = useAuth()
  const query = useQuery({
    queryKey: ['dashboard', 'orders', { page, status }],
    queryFn: () => api.dashboard.orders({ page, ...(status !== 'all' ? { status } : {}) }),
    enabled: Boolean(user),
    retry: false,
    staleTime: 60_000,
    placeholderData: keepPreviousData, // keep current rows visible while the next page loads
    select: (res) => {
      const paginator = unwrap(res) || {}
      return {
        orders: normaliseOrders(Array.isArray(paginator.data) ? paginator.data : []),
        lastPage: paginator.last_page ?? 1,
        total: paginator.total ?? 0,
        counts: res?.counts ?? null,
      }
    },
  })
  const { orders = [], lastPage = 1, total = 0, counts = null } = query.data ?? {}

  // The /orders payload nests each product WITHOUT any image field, so item
  // thumbnails are hydrated from the catalog's single-product read. Cached
  // indefinitely — product images don't change between visits.
  const productIds = [...new Set(orders.flatMap((o) => o.items.map((it) => it.productId)).filter(Boolean))]
  const imageQueries = useQueries({
    queries: productIds.map((id) => ({
      queryKey: ['catalog', 'product-image', id],
      queryFn: () => api.catalog.get(id),
      retry: false,
      staleTime: Infinity,
      select: (res) => {
        const p = unwrap(res) || {}
        return p.image_url || p.media?.[0]?.original_url || null
      },
    })),
  })
  const imageById = Object.fromEntries(productIds.map((id, i) => [id, imageQueries[i].data]))
  const rows = orders.map((o) => ({
    ...o,
    items: o.items.map((it) =>
      it.img || !it.productId ? it : { ...it, img: imageById[it.productId] ?? null },
    ),
  }))

  return {
    orders: rows,
    hasOrders: rows.length > 0,
    isLoading: query.isPending,
    isFetching: query.isFetching,
    isError: query.isError,
    refetch: query.refetch,
    lastPage,
    total,
    /** { all, active, delivered, cancelled, returned }, or null on an older API. */
    counts,
  }
}

/**
 * Puja bookings — Pooja Energization bookings from GET /my-bookings.
 * Fetched lazily by the Puja Bookings tab (like useOrders). The puja API
 * rides its own envelope { success, data, meta } — rows come straight in
 * `data`, pagination in `meta`; list rows omit devotees/address (the
 * details modal fetches the full record per booking).
 */
export function usePujaBookings(page = 1) {
  const { user } = useAuth()
  const query = useQuery({
    queryKey: ['dashboard', 'puja-bookings', { page }],
    queryFn: () => api.puja.myBookings({ page }),
    enabled: Boolean(user),
    retry: false,
    staleTime: 60_000,
    placeholderData: keepPreviousData, // keep current rows visible while the next page loads
    select: (res) => ({
      bookings: Array.isArray(res?.data) ? res.data : [],
      lastPage: res?.meta?.last_page ?? 1,
      total: res?.meta?.total ?? 0,
    }),
  })
  const { bookings = [], lastPage = 1, total = 0 } = query.data ?? {}
  return {
    bookings,
    hasBookings: bookings.length > 0,
    isLoading: query.isPending,
    isFetching: query.isFetching,
    isError: query.isError,
    refetch: query.refetch,
    lastPage,
    total,
  }
}

/** The soonest not-yet-finished class from a course's embedded
 * `upcomingLiveClasses` rows ({date:"YYYY-MM-DD", time:"HH:mm:ss", status}).
 * Unscheduled rows (no parseable date) sort last so a dated class wins. */
function nextLiveClass(list) {
  if (!Array.isArray(list)) return null
  const candidates = list
    .filter((lc) => !['completed', 'cancelled'].includes(String(lc?.status || '').toLowerCase()))
    .map((lc) => {
      const d = lc?.date ? new Date(`${lc.date}T${lc.time || '00:00:00'}`) : null
      return { lc, d: d && !Number.isNaN(d.getTime()) ? d : null }
    })
    .sort((a, b) => (a.d?.getTime() ?? Infinity) - (b.d?.getTime() ?? Infinity))
  if (!candidates.length) return null
  const { lc, d } = candidates[0]
  return {
    title: lc.title || null,
    isNow: String(lc.status || '').toLowerCase() === 'live',
    label: d
      ? `${d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })} · ${d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}`
      : null,
  }
}

/**
 * The seeker's course enrollments (GET /enrollments — Laravel paginator with
 * the course relation nested in the same camelCased shape as the catalog).
 * Powers the overview's Active Courses stat and the Courses tab's
 * "My Enrolled Courses" / "My Enrolled Live Courses" cards: `activeCount` is
 * enrollments still in progress, `enrollments.length` the all-time total,
 * and `isLive` splits live batches from self-paced (recorded) courses.
 */
export function useEnrollments() {
  const { user } = useAuth()
  const query = useQuery({
    queryKey: ['dashboard', 'enrollments'],
    queryFn: () => api.dashboard.enrollments(),
    enabled: Boolean(user),
    retry: false,
    staleTime: 60_000,
    select: (res) =>
      toList(res).map((e, i) => {
        const progress = toNumber(e.progress_percent) ?? 0
        const course = e.course || {}
        const when = e.purchased_at || e.created_at
        const enrolledDate = when ? new Date(when) : null
        const next = nextLiveClass(course.upcomingLiveClasses)
        return {
          key: e.id ?? i,
          courseId: course.id ?? e.course_id ?? null,
          /** Education-app course pages accept slug or numeric id. */
          courseRef: course.slug ?? course.id ?? e.course_id ?? null,
          title: course.title || 'Course',
          img: course.coverImage || null,
          category: course.category || null,
          instructor: primaryInstructor(course)?.name || null,
          /** Live batch vs self-paced recorded course. */
          isLive: String(course.courseType || course.course_type || '').toLowerCase() === 'live',
          nextClassTitle: next?.title ?? null,
          nextClassLabel: next?.label ?? null,
          nextClassIsNow: next?.isNow ?? false,
          progress,
          completed: Boolean(e.course_completed) || progress >= 100,
          enrolledOn:
            enrolledDate && !Number.isNaN(enrolledDate.getTime())
              ? enrolledDate.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
              : null,
          /** Part payment: what is still owed on this course, straight off the
           * enrollment row (GET /enrollments carries a `payment` block for a
           * priced course). Null when there is nothing to pay — a free course or
           * one already settled — so the card shows a Pay CTA only to the people
           * it applies to. */
          payment:
            e.payment && !e.payment.isFullyPaid
              ? {
                  balance: toNumber(e.payment.balance) ?? 0,
                  dueNow: toNumber(e.payment.nextAmount ?? e.payment.balance) ?? 0,
                  dueDate: e.payment.nextInstallment?.due_date ?? null,
                  label: e.payment.nextInstallment?.label ?? null,
                }
              : null,
        }
      }),
  })
  const enrollments = query.data ?? []
  return {
    enrollments,
    activeCount: enrollments.filter((e) => !e.completed).length,
    hasEnrollments: enrollments.length > 0,
    isLoading: query.isPending,
    isError: query.isError,
    refetch: query.refetch,
  }
}

/** Live course record → the Courses tab's browse-card shape. The live API
 * pre-camelCases fields (coverImage, discountPrice, studentsEnrolled…) and
 * `category` is a plain name string. */
function normaliseCourse(c, i) {
  const price = toNumber(c.price)
  const discount = toNumber(c.discountPrice)
  const hasDiscount = discount != null && price != null && discount > 0 && discount < price
  const finalPrice = hasDiscount ? discount : price
  return {
    key: c.id ?? i,
    id: c.id ?? null,
    /** Education-app course pages accept slug or numeric id. */
    courseRef: c.slug ?? c.id ?? null,
    title: c.title || 'Course',
    img: c.coverImage || null,
    instructor: primaryInstructor(c)?.name || null,
    instructorPhoto: primaryInstructor(c)?.photo || null,
    /** All instructors — the hero-style instructor course card lists each one. */
    instructors: courseInstructors(c).map((p) => ({
      id: p?.id ?? null,
      name: p?.name || 'Instructor',
      photo: p?.photo || null,
      designation: p?.designation || null,
    })),
    category: c.category || 'Astrology',
    level: titleCase(c.level),
    badge: String(c.courseType || '').toLowerCase() === 'live' ? '🔴 Live' : titleCase(c.level) || 'Course',
    isLiveCourse: String(c.courseType || '').toLowerCase() === 'live',
    visibility: c.visibility || null,
    lastUpdated: c.lastUpdated || null,
    students: toNumber(c.studentsEnrolled) ?? 0,
    reviews: toNumber(c.totalReviews) ?? 0,
    priceLabel: finalPrice == null || finalPrice === 0 ? 'Free' : `₹${finalPrice.toLocaleString('en-IN')}`,
    oldPriceLabel: hasDiscount ? `₹${price.toLocaleString('en-IN')}` : null,
  }
}

/**
 * Published course catalog for the Courses tab's browse row — lazy, fetched
 * on the tab's first render. GET /courses is a flat API-resource collection
 * with pagination under `meta` (not the shop paginator shape).
 */
export function useCourseCatalog(limit = 8) {
  const query = useQuery({
    queryKey: ['dashboard', 'courses', { limit }],
    queryFn: () => api.courses.list({ per_page: limit }),
    retry: false,
    staleTime: 60_000,
    select: (res) => ({
      // Hard cap at `limit` even if the backend ignores per_page.
      courses: (res?.data ?? []).slice(0, limit).map(normaliseCourse),
      total: res?.meta?.total ?? res?.data?.length ?? 0,
    }),
  })
  const { courses = [], total = 0 } = query.data ?? {}
  return {
    courses,
    total,
    hasCourses: courses.length > 0,
    isLoading: query.isPending,
    isError: query.isError,
    refetch: query.refetch,
  }
}

/**
 * Live classes hosted BY the signed-in instructor — GET /live-classes is
 * scoped server-side (instructors only see their own; admins see all).
 * Fires only for instructor accounts. Fetches one large page so the
 * overview's Live Now/Scheduled/Recordings counts are derived from (almost
 * always) the full set; `total` comes from the paginator when the backend
 * paginates, so Total Classes stays accurate regardless.
 */
export function useLiveClasses() {
  const { user } = useAuth()
  const enabled = isInstructorUser(user)
  const query = useQuery({
    queryKey: ['dashboard', 'live-classes'],
    queryFn: () => api.liveClasses.list({ per_page: 100 }),
    enabled,
    retry: false,
    staleTime: 60_000,
    select: (res) => {
      const d = unwrap(res) || {}
      const rows = Array.isArray(d) ? d : Array.isArray(d.data) ? d.data : []
      return {
        classes: normaliseLiveClasses(rows),
        total: Array.isArray(d) ? rows.length : (d.total ?? rows.length),
      }
    },
  })
  const { classes = [], total = 0 } = query.data ?? {}
  return {
    classes,
    total,
    hasClasses: classes.length > 0,
    isLoading: enabled && query.isPending,
    isError: query.isError,
    refetch: query.refetch,
  }
}

/**
 * Delete one live class (DELETE /live-classes/:id — assigned instructor or
 * Admin only, enforced server-side). On success refreshes every screen the
 * class appears on: the instructor's class list (Academy tab sections and
 * per-card session counts) and any open course-detail page (embedded rows).
 * Per-call onSuccess/onError still work via mutate(id, { ... }).
 */
export function useDeleteLiveClass() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (liveClassId) => api.liveClasses.remove(liveClassId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['dashboard', 'live-classes'] })
      queryClient.invalidateQueries({ queryKey: ['dashboard', 'course-detail'] })
    },
  })
}

/**
 * Courses TAUGHT BY the signed-in instructor. The course API has no
 * instructor-scoped endpoint, so we pull the published catalog (GET /courses)
 * and keep only the ones whose `instructors` array (courses can be
 * co-taught) contains this user — matched STRICTLY by instructor id, never
 * by display name (name matching pulled in other instructors' courses).
 *
 * "This user" is the union of two id sources, because the course payload's
 * instructor ids may not be plain user ids:
 *   1. the auth user's own id, and
 *   2. the instructor_id/instructor.id values on the user's live classes —
 *      GET /live-classes is scoped server-side to the signed-in instructor,
 *      so any instructor id appearing there is authoritatively "me" in the
 *      LMS's id space. (Admins would see everyone's classes, but this hook
 *      only runs for instructor-role accounts.)
 *
 * The backend ignores per_page (always its own default), so every page is
 * fetched via meta.last_page rather than trusting one oversized request.
 * Fires only for instructor accounts. Drives the Academy tab's "By Course"
 * section, so a course with no scheduled live classes still shows up.
 *
 * ⚠ Known gaps needing backend support: GET /courses returns PUBLISHED
 * courses only (drafts can't appear until published), and the payload has no
 * created_by/owner field — a course listing you as co-instructor is
 * indistinguishable from one you created.
 */
export function useInstructorCourses() {
  const { user } = useAuth()
  const enabled = isInstructorUser(user) && Boolean(user?.id)
  const query = useQuery({
    queryKey: ['dashboard', 'instructor-courses', user?.id],
    queryFn: async ({ signal }) => {
      const [first, liveRes] = await Promise.all([
        api.courses.list({ page: 1 }, { signal }),
        // Best-effort identity source — the course filter still works from
        // the user id alone if the live-class read fails.
        api.liveClasses.list({ per_page: 100 }).catch(() => null),
      ])
      const rows = Array.isArray(first?.data) ? [...first.data] : []
      const lastPage = Math.min(Number(first?.meta?.last_page) || 1, 10)
      if (lastPage > 1) {
        const rest = await Promise.all(
          Array.from({ length: lastPage - 1 }, (_, i) => api.courses.list({ page: i + 2 }, { signal })),
        )
        for (const res of rest) if (Array.isArray(res?.data)) rows.push(...res.data)
      }
      const liveData = unwrap(liveRes) || {}
      const liveRows = Array.isArray(liveData) ? liveData : Array.isArray(liveData.data) ? liveData.data : []
      const myInstructorIds = []
      for (const lc of liveRows) {
        if (lc?.instructor_id != null) myInstructorIds.push(String(lc.instructor_id))
        if (lc?.instructor?.id != null) myInstructorIds.push(String(lc.instructor.id))
      }
      return { rows, myInstructorIds }
    },
    enabled,
    retry: false,
    staleTime: 60_000,
    select: ({ rows, myInstructorIds }) => {
      const myIds = new Set([String(user?.id ?? ''), ...myInstructorIds])
      myIds.delete('')
      const mine = rows.filter((c) =>
        courseInstructors(c).some((inst) => inst?.id != null && myIds.has(String(inst.id))),
      )
      return mine.map(normaliseCourse)
    },
  })
  const courses = query.data ?? []
  return {
    courses,
    hasCourses: courses.length > 0,
    isLoading: enabled && query.isPending,
    isError: query.isError,
    refetch: query.refetch,
  }
}

/**
 * Course categories for the instructor create-course form's category select
 * (GET /course-categories). The read-side course API exposes only category
 * NAMES on each course, not ids, so authoring needs this dedicated lookup.
 * Returns react-select-ready `{ value, label }` options; cached for a while
 * since categories rarely change.
 */
export function useCourseCategories() {
  const query = useQuery({
    queryKey: ['dashboard', 'course-categories'],
    queryFn: () => api.courses.categories(),
    retry: false,
    staleTime: 5 * 60_000,
    select: (res) =>
      toList(res)
        .filter((c) => c && c.id != null)
        .map((c) => ({ value: c.id, label: c.name || c.title || `Category ${c.id}` })),
  })
  const categories = query.data ?? []
  return {
    categories,
    hasCategories: categories.length > 0,
    isLoading: query.isPending,
    isError: query.isError,
  }
}

/**
 * Full detail record of one course (GET /courses/:id) — the instructor
 * course-details page's data source. Returns the raw camelCased course
 * (curriculum sessions with lessons, embedded live classes/recordings and
 * every meta field) — the page derives its own display bits. Shares the
 * ['dashboard','course-detail',id] cache with useCourseTopics below, so the
 * Add Lesson modal and the details page never double-fetch.
 */
export function useCourseDetails(courseId) {
  const enabled = Boolean(courseId)
  const query = useQuery({
    queryKey: ['dashboard', 'course-detail', String(courseId)],
    queryFn: () => api.courses.get(courseId),
    enabled,
    retry: false,
    staleTime: 60_000,
    select: (res) => unwrap(res) || null,
  })
  return {
    course: query.data ?? null,
    isLoading: enabled && query.isPending,
    isError: query.isError,
    refetch: query.refetch,
  }
}

/**
 * Existing topics/modules of one course, for the Add Lesson form's "attach to
 * a module" select. The course-detail read already groups lessons into
 * `curriculum` sessions server-side, so each session is a topic; fires only
 * once a course is chosen. Returns react-select-ready `{ value, label }`.
 * Same query key as useCourseDetails — react-query caches the raw response
 * once and applies each hook's `select` per observer.
 */
export function useCourseTopics(courseId) {
  const enabled = Boolean(courseId)
  const query = useQuery({
    queryKey: ['dashboard', 'course-detail', String(courseId)],
    queryFn: () => api.courses.get(courseId),
    enabled,
    retry: false,
    staleTime: 60_000,
    select: (res) => {
      const course = unwrap(res) || {}
      const sessions = Array.isArray(course.curriculum) ? course.curriculum : []
      return sessions
        .filter((s) => s && s.id != null)
        .map((s) => ({ value: s.id, label: s.title || `Module ${s.id}` }))
    },
  })
  const topics = query.data ?? []
  return {
    topics,
    hasTopics: topics.length > 0,
    isLoading: enabled && query.isPending,
    isError: query.isError,
  }
}

/**
 * Order count for the overview stat — same query key as the Orders tab's
 * first page, so the fetch is shared/cached and switching to Orders later
 * costs nothing extra; only the `select` differs (total, not rows).
 */
export function useOrdersCount() {
  const { user } = useAuth()
  const query = useQuery({
    queryKey: ['dashboard', 'orders', { page: 1 }],
    queryFn: () => api.dashboard.orders({ page: 1 }),
    enabled: Boolean(user),
    retry: false,
    staleTime: 60_000,
    select: (res) => (unwrap(res) || {}).total ?? 0,
  })
  return { total: query.data ?? 0, isLoading: query.isPending }
}

/**
 * Puja booking count for the overview stat — same query key as the Puja
 * Bookings tab's first page (usePujaBookings), so the fetch is shared and
 * switching to the tab later costs nothing extra; only `select` differs
 * (the meta total, not rows).
 */
export function usePujaBookingsCount() {
  const { user } = useAuth()
  const query = useQuery({
    queryKey: ['dashboard', 'puja-bookings', { page: 1 }],
    queryFn: () => api.puja.myBookings({ page: 1 }),
    enabled: Boolean(user),
    retry: false,
    staleTime: 60_000,
    select: (res) => res?.meta?.total ?? (Array.isArray(res?.data) ? res.data.length : 0),
  })
  return { total: query.data ?? 0, isLoading: query.isPending }
}

/**
 * Birth details off the shared profile read (['dashboard','me'] — dedupes
 * with useDashboard/useAstrology) — the gate for every personal VedAstro
 * calculation. Returns null fields until the profile lands or when the
 * account has no stored birth date.
 */
function useBirthDetails() {
  const { user } = useAuth()
  const profileQuery = useQuery({
    queryKey: ['dashboard', 'me'],
    queryFn: () => api.dashboard.me(),
    enabled: Boolean(user),
    retry: false,
    select: unwrap,
  })
  const pd = profileQuery.data || {}
  return {
    birthDob: normaliseDob(pd.dob || pd.date_of_birth),
    birthTime: normaliseTime(pd.birth_time),
    birthPlace: pd.birth_place || '',
    profileLoading: profileQuery.isPending,
  }
}

/**
 * Full VedAstro birth-chart prediction report (HoroscopePredictions) for the
 * Reports tab — ~100+ classical predictions tagged by life area. Only fires
 * when the profile carries a birth date; cached indefinitely per birth
 * details, so revisits are free.
 */
export function useHoroscopePredictions() {
  const { birthDob, birthTime, birthPlace, profileLoading } = useBirthDetails()
  const query = useQuery({
    queryKey: ['astro', 'predictions', birthDob, birthTime, birthPlace],
    enabled: Boolean(birthDob),
    retry: false,
    staleTime: Infinity,
    queryFn: async ({ signal }) => {
      const location = await resolveLocation(birthPlace, signal)
      return getHoroscopePredictions({ dob: birthDob, tob: birthTime, location }, signal)
    },
  })
  return {
    predictions: query.data ?? [],
    hasBirthDetails: Boolean(birthDob),
    profileLoading,
    isLoading: Boolean(birthDob) && query.isPending,
    isError: query.isError,
    error: query.error ? (query.error.message || 'Could not load your report.') : null,
    refetch: query.refetch,
  }
}

/**
 * Shadbala strength + Ishta/Kashta scores for the seven classical grahas
 * (getPlanetStrengths) — what the Remedies tab uses to decide which planets
 * need support. Same birth-details gating and forever-cache as the
 * predictions report.
 */
export function usePlanetStrengths() {
  const { birthDob, birthTime, birthPlace, profileLoading } = useBirthDetails()
  const query = useQuery({
    queryKey: ['astro', 'strengths', birthDob, birthTime, birthPlace],
    enabled: Boolean(birthDob),
    retry: false,
    staleTime: Infinity,
    queryFn: async ({ signal }) => {
      const location = await resolveLocation(birthPlace, signal)
      return getPlanetStrengths({ dob: birthDob, tob: birthTime, location }, signal)
    },
  })
  return {
    strengths: query.data ?? [],
    hasBirthDetails: Boolean(birthDob),
    profileLoading,
    isLoading: Boolean(birthDob) && query.isPending,
    isError: query.isError,
    error: query.error ? (query.error.message || 'Could not compute planetary strengths.') : null,
    refetch: query.refetch,
  }
}

/**
 * Astrology (VedAstro) block — panchang, live transits and the seeker's
 * personal kundli/horoscope. Extracted from useDashboard so only the tabs
 * that render astrology (Overview variants, Horoscope, Kundli) trigger the
 * upstream calls — landing on Orders/Appoinments no longer fires the
 * PanchangaTable/PlanetRasiD1Sign/MoonSignName request fan-out. The shared
 * query keys (including ['dashboard','me']) dedupe across tabs and with
 * useDashboard itself. Returns the same `astro` shape tabs always consumed.
 */
export function useAstrology() {
  const { user } = useAuth()

  // Birth details ride on the profile read — same key as useDashboard's,
  // so this never adds a second /me call.
  const profileQuery = useQuery({
    queryKey: ['dashboard', 'me'],
    queryFn: () => api.dashboard.me(),
    enabled: Boolean(user),
    retry: false,
    select: unwrap,
  })

  // Public data, best-effort, cached for the day.
  const astroDate = new Date().toISOString().slice(0, 10)
  const pd = profileQuery.data || {}
  const birthDob = normaliseDob(pd.dob || pd.date_of_birth)
  const birthTime = normaliseTime(pd.birth_time)
  const birthPlace = pd.birth_place || ''

  const panchangQuery = useQuery({
    queryKey: ['astro', 'panchang', astroDate],
    queryFn: () => getPanchang({ date: astroDate }),
    retry: false,
    staleTime: 6 * 60 * 60 * 1000,
  })

  const transitsQuery = useQuery({
    queryKey: ['astro', 'transits', astroDate],
    queryFn: () => getPlanetTransits(astroDate),
    retry: false,
    staleTime: 6 * 60 * 60 * 1000,
  })

  // Personal chart — only when the profile carries a usable birth date.
  const kundliQuery = useQuery({
    queryKey: ['astro', 'kundli', birthDob, birthTime, birthPlace],
    enabled: Boolean(birthDob),
    retry: false,
    staleTime: Infinity,
    queryFn: async () => {
      const location = await resolveLocation(birthPlace)
      return getKundli({ dob: birthDob, tob: birthTime, location })
    },
  })

  // ── Derive the astrology view-data from the live queries. ──
  const panchang = panchangQuery.data || null
  const transits = transitsQuery.data || null
  const kundli = kundliQuery.data || null
  const moonSign = kundli?.moonSign || null

  // Personal daily horoscope needs the user's Moon sign (from their chart).
  const horoscope = moonSign && transits ? buildTransitReading(moonSign, 'today', transits) : null

  // Planetary influences read from the live sky; favourability needs the Rashi.
  const planets = transits
    ? ['Moon', 'Mars', 'Mercury', 'Venus', 'Jupiter', 'Saturn'].map((name) => {
        const t = transits[name]
        const st = moonSign && t ? planetTransitStatus(name, t.sign, moonSign) : null
        return {
          name,
          icon: PLANET_ICON[name],
          sign: t?.sign || '—',
          note: st?.note || '',
          status: st?.label || null,
          favourable: st?.favourable ?? null,
        }
      })
    : null

  return {
    panchang,
    transits,
    planets,
    kundli: kundli
      ? {
          ...kundli,
          moonGlyph: SIGN_GLYPH[kundli.moonSign] || '',
          lagnaGlyph: SIGN_GLYPH[kundli.lagna] || '',
          sunGlyph: SIGN_GLYPH[kundli.sunSign] || '',
          chart: buildKundliChart(kundli), // 12 North-Indian house cells w/ grahas
        }
      : null,
    moonSign,
    moonGlyph: moonSign ? SIGN_GLYPH[moonSign] : null,
    horoscope, // { text, luckyNum, luckyColor, luckyMood, energy }
    hasKundli: Boolean(kundli),
    hasHoroscope: Boolean(horoscope),
    // Kundli is only attempted when a birth date exists; surface its progress
    // so the Kundli tab can show a live loading/empty state instead of demo data.
    hasBirthDetails: Boolean(birthDob),
    kundliLoading: Boolean(birthDob) && kundliQuery.isFetching,
    kundliError: kundliQuery.error ? (kundliQuery.error.message || 'Could not load your chart.') : null,
    isLoading: panchangQuery.isPending || transitsQuery.isPending,
  }
}

/* ── the hook ── */

export function useDashboard(authUser) {
  const enabled = Boolean(authUser)

  const profileQuery = useQuery({
    queryKey: ['dashboard', 'me'],
    queryFn: () => api.dashboard.me(),
    enabled,
    retry: false,
    select: unwrap,
  })

  const bookingsQuery = useQuery({
    queryKey: ['dashboard', 'bookings'],
    queryFn: () => api.dashboard.bookings(),
    enabled,
    retry: false,
    select: (res) => normaliseConsultations(toList(res)),
  })

  // Astrologer accounts: the profile photo lives on the astrologer record,
  // not the session user. Same query key + select as the My Profile tab, so
  // react-query dedupes the read when both are on screen.
  const astrologerQuery = useQuery({
    queryKey: ['dashboard', 'astrologer-profile', authUser?.id],
    queryFn: () => api.astrologers.profile(),
    enabled: enabled && isAstrologerUser(authUser) && Boolean(authUser?.id),
    retry: false,
    select: unwrapAstrologerProfile,
  })
  const astroRec = astrologerQuery.data || {}

  // Merge the SSO session user with the richer profile read; auth wins for identity.
  const p = profileQuery.data || {}
  const fullName = [p.first_name, p.middle_name, p.last_name].filter(Boolean).join(' ').trim()
  const profile = {
    name: authUser?.name || fullName || p.name || 'Seeker',
    email: authUser?.email || p.email || p.corp_email || p.alt_email || '',
    mobile: authUser?.mobile || p.mobile_no || p.mobile || p.alt_ph || '',
    dob: p.dob || p.date_of_birth || '',
    // time_of_birth/place_of_birth are what /user returns (edited on My Profile);
    // birth_time/birth_place are older aliases kept as fallbacks.
    birthTime: p.is_birth_time_unknown ? '' : p.time_of_birth || p.birth_time || '',
    birthPlace: p.place_of_birth || p.birth_place || p.city || '',
    // The site takes City as the place of birth; place_of_birth only comes
    // from the mobile app's registration.
    birthTimeUnknown: Boolean(p.is_birth_time_unknown),
    hasBirthPlace: Boolean(p.place_of_birth || p.birth_place || p.city),
  }

  const consultations = bookingsQuery.data ?? []

  return {
    profile,
    initials: initials(profile.name),
    membership: membershipLabel(authUser?.roles),
    // profile_image first: it's the fresh /user read the My Profile uploader
    // invalidates, where the session user only catches up on reload.
    avatarUrl: p.profile_image || authUser?.avatarUrl || astroRec.image_url || astroRec.photo_url || astroRec.photo || null,
    // Earned on shop purchases (0.5 per ₹50); null until the /user read lands.
    karmaPoints: toNumber(p.karma_points),
    consultations,
    hasConsultations: consultations.length > 0,
    isLoading: profileQuery.isPending || bookingsQuery.isPending,
    profileLoading: profileQuery.isPending,
  }
}
